import { useSelector } from 'react-redux';
import { selectors as basketNewOrderSelectors } from '@store/basket/basketNewOrder.slice';
import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import orderBy from 'lodash/orderBy';
import type { Calorific } from 'types/Calorific';
import type { OrderForm } from 'types/OrderForm';
import type { Variant } from 'types/Variant';
import BASKET_PAYMENT_MODES from '@constants/basketPaymentModes';
import { getIDfromIRI } from '@services/Api.service';

type PricesByType = {
  standard?: number;
  test?: number;
  subscription?: number;
};

type DiscountParts = { [key: string]: number | undefined };

export type StaticBasketDietElement = {
  dietId: string | null;
  variantId: string | null;
  calorificId: string | null;
  dietLength?: number;
  mealTypes?: string[];
  paymentMode?: string;
  useEcoContainers?: boolean;
  optionChangeMenu?: boolean;
};

export type StaticBasketPricePerDay = {
  dietDayPrice: number;
  dayBeforeDiscount: number;
  dayAfterDiscount: number;
  dietBeforeDiscount: number;
  optionPremium: number | null;
  optionEcoContainers: number | null;
  optionContainers: number | null | undefined;
};

export type StaticBasketRowPrices = {
  dietPrices: { beforeDiscount: number; afterDiscount: number };
  pricePerDay: StaticBasketPricePerDay;
  discountParts: DiscountParts;
  priceDiscounts: number;
  past30DaysLowestPrice: { DIET: number };
};

export type StaticBasketPrices = {
  price: {
    beforeDiscount: number;
    afterDiscount: number;
    discountParts: DiscountParts;
  };
  rowsPrices: StaticBasketRowPrices[];
};

type GetBasketPricesParams = {
  dietElements: StaticBasketDietElement[];
  testMode: boolean;
  queryClient: QueryClient;
};

type PriceAfterDiscountParams = {
  price: number;
  discountBase: number;
  dietLength: number;
  isSubscriptionPayment: boolean;
};

const sumValues = (obj: DiscountParts) =>
  Object.values(obj).reduce<number>((sum, value) => sum + (value ?? 0), 0);

const getPriceByType = (
  prices: PricesByType,
  isSubscriptionPayment: boolean,
  testMode: boolean
) => {
  if (isSubscriptionPayment) {
    return prices.subscription ?? 0;
  }

  return (testMode ? prices.test : prices.standard) ?? 0;
};

const getSuperVariantResignPrice = (
  currentCalorific: Calorific | undefined,
  selectedMealTypes: string[] = []
) =>
  (currentCalorific?.superVariantMealType ?? []).reduce(
    (sum, { mealType, resignPrice }) =>
      selectedMealTypes.includes(mealType) ? sum : sum + (resignPrice ?? 0),
    0
  );

const getSuperVariantDayPrice = (
  dayPrice: number,
  currentVariant: Variant,
  currentCalorific: Calorific | undefined,
  selectedMealTypes: string[] | undefined
) => {
  const dayPriceMinusResignPrice =
    dayPrice - getSuperVariantResignPrice(currentCalorific, selectedMealTypes);
  const minPrice = currentVariant.minPrice ?? 0;

  return dayPriceMinusResignPrice > minPrice
    ? dayPriceMinusResignPrice
    : minPrice;
};

export const getBasketPrices = ({
  dietElements,
  testMode,
  queryClient,
}: GetBasketPricesParams): StaticBasketPrices => {
  const orderForm = queryClient.getQueryData<OrderForm>(['orderForm']);
  const diets = orderForm?.diets ?? [];
  const additionalPrices = orderForm?.additionalPrices;
  const priceListByKey = orderForm?.priceList?.indexedByKey;

  const sortedDietLengthDiscounts = orderBy(
    orderForm?.discounts?.DIET_LENGTH ?? [],
    ['minLength', 'maxLength'],
    ['asc', 'asc']
  );

  const getPriceAfterDiscount = ({
    price,
    discountBase,
    dietLength,
    isSubscriptionPayment,
  }: PriceAfterDiscountParams): {
    price: number;
    discountParts: DiscountParts;
  } => {
    const dietLengthDiscount = sortedDietLengthDiscounts.find(
      ({ minLength, maxLength }) =>
        dietLength >= minLength && dietLength <= maxLength
    );

    if (isSubscriptionPayment || !dietLengthDiscount) {
      return { price, discountParts: {} };
    }

    const { isPercentage, discount } = dietLengthDiscount;
    const dayDiscount = isPercentage
      ? discountBase * (discount / 100)
      : Math.min(discount, discountBase);

    return {
      price: price - dayDiscount,
      discountParts: { DIET_LENGTH: dayDiscount * dietLength },
    };
  };

  return dietElements.reduce<StaticBasketPrices>(
    (basketPrices, dietElement) => {
      const isSubscriptionPayment =
        dietElement.paymentMode === BASKET_PAYMENT_MODES.SUBSCRIPTION_PAYMENT;

      const dietId = getIDfromIRI(dietElement.dietId);
      const variantId = getIDfromIRI(dietElement.variantId);
      const calorificId = getIDfromIRI(dietElement.calorificId);

      const currentDiet = diets.find(
        ({ '@id': iri }) => iri === dietElement.dietId
      );
      const currentVariant = currentDiet?.variants?.find(
        ({ '@id': iri }) => iri === dietElement.variantId
      );
      const currentCalorific = currentVariant?.calories?.find(
        ({ '@id': iri }) => iri === dietElement.calorificId
      );

      const dietPrice =
        priceListByKey?.[`${dietId}_${variantId}_${calorificId}`];

      const basePricePerDay = getPriceByType(
        dietPrice ?? {},
        isSubscriptionPayment,
        testMode
      );
      const pricePerDayByType = currentVariant?.superVariantBeta
        ? getSuperVariantDayPrice(
            basePricePerDay,
            currentVariant,
            currentCalorific,
            dietElement.mealTypes
          )
        : basePricePerDay;

      const past30DaysLowestPriceByType = getPriceByType(
        dietPrice?.past30DaysLowestPrice ?? {},
        isSubscriptionPayment,
        testMode
      );

      const dietLength = dietElement.dietLength ?? 0;

      const containersCount = currentVariant?.superVariantBeta
        ? dietElement.mealTypes?.length ?? 0
        : currentVariant?.mealTypes?.length ?? 0;

      const ecoContainerPrice = dietElement.useEcoContainers
        ? (additionalPrices?.ECO_CONTAINER.cost ?? 0) * containersCount
        : 0;

      const containerCost = additionalPrices?.CONTAINER?.cost;
      const containersPrices = containerCost
        ? containerCost * containersCount
        : containerCost;

      const actualContainerPrices =
        (dietElement.useEcoContainers ? ecoContainerPrice : containersPrices) ||
        0;

      const optionPremiumPrice = dietElement.optionChangeMenu
        ? additionalPrices?.CHANGE_MENU?.cost ?? 0
        : 0;

      const dayBeforeDiscount =
        pricePerDayByType + optionPremiumPrice + actualContainerPrices;

      const { price: dayAfterDiscount, discountParts } = getPriceAfterDiscount({
        price: dayBeforeDiscount,
        discountBase: pricePerDayByType + optionPremiumPrice,
        dietLength,
        isSubscriptionPayment,
      });

      const mergedDiscountParts = Object.entries(
        discountParts
      ).reduce<DiscountParts>(
        (merged, [key, value]) => ({
          ...merged,
          [key]: (merged[key] ?? 0) + (value ?? 0),
        }),
        { ...basketPrices.price.discountParts }
      );

      const pricePerDay: StaticBasketPricePerDay = {
        dietDayPrice: pricePerDayByType,
        dayBeforeDiscount,
        dayAfterDiscount,
        dietBeforeDiscount: pricePerDayByType,
        optionPremium: dietElement.optionChangeMenu ? optionPremiumPrice : null,
        optionEcoContainers: dietElement.useEcoContainers
          ? ecoContainerPrice
          : null,
        optionContainers: containerCost === null ? null : containersPrices,
      };

      const dietPrices = {
        beforeDiscount: dayBeforeDiscount * dietLength,
        afterDiscount: dayAfterDiscount * dietLength,
      };

      return {
        price: {
          beforeDiscount:
            basketPrices.price.beforeDiscount + dietPrices.beforeDiscount,
          afterDiscount:
            basketPrices.price.afterDiscount + dietPrices.afterDiscount,
          discountParts: mergedDiscountParts,
        },
        rowsPrices: [
          ...basketPrices.rowsPrices,
          {
            dietPrices,
            pricePerDay,
            discountParts,
            priceDiscounts: sumValues(discountParts),
            past30DaysLowestPrice: { DIET: past30DaysLowestPriceByType },
          },
        ],
      };
    },
    {
      price: {
        beforeDiscount: 0,
        afterDiscount: 0,
        discountParts: {},
      },
      rowsPrices: [],
    }
  );
};

const useBasketPrices = ({
  dietElements,
}: {
  dietElements: StaticBasketDietElement[];
}) => {
  const queryClient = useQueryClient();
  const { testMode } = useSelector(basketNewOrderSelectors.selectBasket) as {
    testMode: boolean;
  };

  return getBasketPrices({ dietElements, testMode, queryClient });
};

export default useBasketPrices;
